import ErrorIcon from 'components/ReusableComponents/BackgroundImage/ErrorIcon'
import { Component, createRef } from 'react'
import { Mention, MentionsInput } from 'react-mentions'

import { setCaretPosition } from '../../utils/helpers'
import styles from './EditTextDialog.module.scss'
import { EmojiPickerContainer } from './EmojiPickerContainer'
import { mentionInputDisabledStyle, mentionInputStyle } from './mentionsInputStyle'
import { optOutText } from './utils/textContentStrings'

const OPT_OUT_TEXT_LENGTH = 22
const MAX_LENGTH = 950

function getTagLessText(text) {
  return text
    .replace(/@\[?[a-zA-Z]+\]\((__)?[a-zA-Z]+(__)?\)/gi, '')
    .replace(/guestName|hotelName|inviteLink/gi, '')
    .replace(/Text STOP for NO text\./gi, '')
    .trim()
}

class TextArea extends Component {
  static defaultProps = {
    previewPrefix: ''
  }

  constructor(props) {
    super(props)
    this.inputRef = createRef()
  }
  state = {
    value: '',
    caretPos: 0,
    baseTags: [...this.props.baseTags],
    usedTags: [...this.props.baseTags].filter(tag => this.props.value.includes(tag.id)),
    remainingRequiredTags: [...this.props.baseTags]
      .filter(tag => this.props.requiredTagIds.includes(tag.id))
      .filter(tag => !this.props.value.includes(tag.id)),
    currentCharCount: 0,
    segments: 1
  }

  updateSegmentsAndCharCount() {
    const realCharCount =
      getTagLessText(this.props.value).length +
      this.getVariableCost() +
      this.props.previewPrefix.length
    this.setState({
      realCharCount
    })
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.state.usedTags.length !== prevState.usedTags.length) {
      if (this.props.handleUpdateUsedTags) {
        this.props.handleUpdateUsedTags(this.state.usedTags)
      }
    }
  }

  componentDidMount() {
    const { value } = this.props

    this.updateSegmentsAndCharCount()
    this.setState({
      value: value.replace(optOutText, ''),
      currentCharCount: value.replace(optOutText, '').replace(/@\S+\)/gm, '').length
    })
    this.inputRef.current.addEventListener('blur', e => {
      const caretPos = e.target.selectionStart
      this.setState({ caretPos })
    })
  }

  getTags = () => {
    const { baseTags, usedTags } = this.state
    const cleanUsedTags = usedTags.map(usedTag => usedTag.display)
    return baseTags.filter(baseTag => !cleanUsedTags.includes(baseTag.display))
  }

  handleChangeLength = length => {
    this.setState({ currentCharCount: length })
  }

  handleChange = (e, newValue, newPlainTextValue, mentions) => {
    const value = e.target.value.slice(0, MAX_LENGTH)
    const remainingRequiredTags = [...this.props.baseTags]
      .filter(tag => this.props.requiredTagIds.includes(tag.id))
      .filter(tag => !newValue.includes(tag.id))

    const cleanText = getTagLessText(newPlainTextValue)
    const cleanTextLength = cleanText.length
    this.handleChangeLength(cleanTextLength)
    this.props.handleChange(newValue.slice(0, MAX_LENGTH))
    this.setState({
      value: value,
      usedTags: mentions,
      remainingRequiredTags
    })

    this.props.handleUpdateUsedTags(mentions)
  }

  handleEmojiClick = emoji => {
    const { caretPos } = this.state
    const value = this.inputRef.current.value
    const valueWithEmoji = [
      value.substring(0, caretPos),
      emoji,
      value.substring(caretPos)
    ].join('')
    const valueWithVars = valueWithEmoji.replace(
      /(guestName|hotelName|checkinDate|checkoutDate|promoName)/g,
      '@[$1](__$1__)'
    )
    this.setState({ value: valueWithVars })
    this.props.handleChange(valueWithVars, value)
    setTimeout(() => {
      setCaretPosition(this.inputRef.current, caretPos + emoji.length)
    }, 0)
  }

  handleEmojiOpen = () => {
    const { caretPos } = this.state
    setTimeout(() => {
      setCaretPosition(this.inputRef.current, caretPos)
    }, 0)
  }

  handleUnusedTagClick = tag => {
    const { caretPos } = this.state
    setCaretPosition(this.inputRef.current, caretPos)
    const value = this.inputRef.current.value

    const valueWithTag = [
      value.substring(0, caretPos),
      tag.display,
      ' ',
      value.substring(caretPos)
    ].join('')

    const valueWithVars = valueWithTag.replace(
      /(guestName|hotelName|checkinDate|checkoutDate|promoName)/g,
      '@[$1](__$1__)'
    )

    const remainingRequiredTags = [...this.props.baseTags]
      .filter(tag => this.props.requiredTagIds.includes(tag.id))
      .filter(tag => !valueWithVars.includes(tag.id))

    this.setState({
      value: valueWithVars,
      remainingRequiredTags,
      usedTags: [...this.state.usedTags, tag]
    })
    this.props.handleChange(valueWithVars, value)

    setTimeout(() => {
      setCaretPosition(this.inputRef.current, caretPos + tag.display.length + 1)
    })
  }

  getVariableCost = () => {
    const optOutTextLength = this.props.shouldShowOptOutText ? OPT_OUT_TEXT_LENGTH : 0
    const cleanUsedTags = this.state.usedTags.map(usedTag => usedTag.display)
    const totalUsedTagsLength = this.state.baseTags
      .filter(baseTag => cleanUsedTags.includes(baseTag.display))
      .reduce((prev, curr) => {
        return prev + curr.length
      }, 0)

    return totalUsedTagsLength + optOutTextLength
  }

  render() {
    return (
      <>
        <div
          className={
            this.props?.disabled ? styles.textareaHolderDisabled : styles.textareaHolder
          }
          style={{ borderColor: this.props.isErred ? 'red' : '#c0b8b8' }}
        >
          <MentionsInput
            value={this.state.value}
            onChange={this.handleChange}
            disabled={this.props?.disabled}
            style={this.props?.disabled ? mentionInputDisabledStyle : mentionInputStyle}
            inputRef={this.inputRef}
            placeholder="Type your message here"
            maxLength={MAX_LENGTH}
            onFocus={this?.props?.onFocus}
            autoFocus={this?.props?.autoFocus}
          >
            <Mention
              trigger="##"
              data={this.getTags}
              className={styles.mentions}
              appendSpaceOnAdd={true}
            />
          </MentionsInput>
          {!this.props?.disabled && (
            <div className={styles.textareaFooter}>
              <EmojiPickerContainer
                onSelect={this.handleEmojiClick}
                onOpen={this.handleEmojiOpen}
              />
              {this.state.remainingRequiredTags.length > 0 && (
                <div className={styles.remainingRequiredTags}>
                  <p className={styles.requiredTagsLabel}>Required tags</p>
                  {this.state.remainingRequiredTags.map(tag => (
                    <div
                      onClick={() => this.handleUnusedTagClick(tag)}
                      key={tag.id}
                      className={styles.requiredTag}
                    >
                      {tag.display}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        {this.state.value.match(
          /(?:(http|https):\/\/)?(?:[\w-]+\.)+[a-z]{2,6}(\S+)?$/
        ) && (
          <p className={styles.recommendation}>
            <ErrorIcon />
            Messages with short links may be marked as spam.
          </p>
        )}
      </>
    )
  }
}

export default TextArea
